import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Grid,
    useTheme,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import Toggle from '../../components/toggle';
import { ORGANIZATION_TYPES, getRegionOptions } from '../../constants';
import { organizationAPI, membershipAPI, didimAPI, logAPI, rankAPI, userAPI, championAPI, physicalRecordAPI } from '../../actions/imsServer';
import { formatDateTimeDisplay, getTodayStartTime, formatTime, isAdmin, toDisplayString } from '../../utils/base';
import StatisticsChart from '../../components/charts/barChart/StatisticsChart';
import { Prev, Next, OrganizationEmblem } from '../../assets/images';
import { ORGANIZATION_RANKING_TYPES, POINT_LOG_TYPES, RANKING_TYPE_TOGGLE_OPTIONS, API_PAGE_SIZE } from '../../constants/index';
import { useLevels } from '../../context/levels';
import RewardHistoryItem from '../../components/rewardHistoryItem';
import { getTableStyles } from '../../components/table/styles';
import ProgressBar from '../../components/charts/progressBar';

// 지표 카드 컴포넌트
const MetricCard = ({ label, value, children, sx = {}, onClick = () => { } }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                bgcolor: theme.palette.figma.block,
                border: `1px solid ${theme.palette.figma.white[20]}`,
                borderRadius: '8px',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                position: 'relative',
                height: '100%',
                ...sx,
                cursor: 'pointer',
                '&:hover': {
                    borderColor: theme.palette.figma.white[40],
                },
            }}
            onClick={onClick}
        >
            {children || (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.figma.white[80], fontSize: '12px' }}>
                            {label}
                        </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>
                        {value}
                    </Typography>
                </>
            )}
        </Box>
    );
};

const OrganizationDetail = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { organizationLevels, loading: levelsLoading } = useLevels();

    const [originOrganization, setOriginOrganization] = useState(location.state?.organization);
    const [organization, setOrganization] = useState(null);

    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo') || '{}'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 기관 통계 데이터
    const [stats, setStats] = useState({
        ranking: null,
        activeChallenges: 0,
        dataSharers: 0,
        todayParticipants: 0,
        todayTotalUsage: 0,
        todayAvgSteps: 0,
    });

    // 진행 바 데이터
    const [progressBars, setProgressBars] = useState([]);

    // 차트 데이터
    const [chartTab, setChartTab] = useState('participants'); // participants, usage, steps, points
    const [chartPeriod, setChartPeriod] = useState('monthly'); // monthly, yearly
    // 기본값: 이번 달의 첫 번째 날
    const [chartCurrentDate, setChartCurrentDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [chartData, setChartData] = useState([]);

    // 경험치 상세보기 팝업 상태
    const [expDetailOpen, setExpDetailOpen] = useState(false);
    const [expCurrentDate, setExpCurrentDate] = useState(new Date());
    const [expPointLogData, setExpPointLogData] = useState([]);

    // 포인트 상세보기 팝업 상태
    const [pointDetailOpen, setPointDetailOpen] = useState(false);
    useState(new Date()); // pointCurrentDate state reserved for future use
    const [pointLogData, setPointLogData] = useState([]);
    const [pointPeriod, setPointPeriod] = useState(7); // 기간 필터 (7일, 30일 등)
    const [pointTypeFilter, setPointTypeFilter] = useState(-1); // 유형 필터 (-1: 전체, 0: 적립, 1: 소멸, 2: 사용)

    // 사용자 랭킹 (0: 누적, 1: 이달)
    const [rankingTypeIndex, setRankingTypeIndex] = useState(0);
    const [selectedDivisionType, setSelectedDivisionType] = useState('all'); // all / division_1 / division_2
    const [selectedDivision1, setSelectedDivision1] = useState('all'); // division1 name or 'all'
    const [selectedDivision2, setSelectedDivision2] = useState('all'); // division2 name or 'all'
    const [userRankings, setUserRankings] = useState([]);

    // 기관 랭킹 타입 (인원수랭킹/누적활동랭킹)
    const [organizationRankingType, setOrganizationRankingType] = useState(1); // 0: 인원수랭킹, 1: 누적활동랭킹 (기본값)
    const [organizationRanking, setOrganizationRanking] = useState(null);
    // 기관 랭킹 값 캐시 (type 0/1 한 번씩만 로드 후 재사용)
    const [organizationRankingByType, setOrganizationRankingByType] = useState({ 0: null, 1: null });

    // 전체 사용자 목록 (한 번만 가져와서 재사용)
    const [allUsers, setAllUsers] = useState([]);
    // 기관 멤버십 (한 번만 가져와서 fetchUserRankings, fetchChartDataForPeriod에서 재사용)
    const [organizationMemberships, setOrganizationMemberships] = useState([]);
    // 사용자 랭킹·기여도 (한 번만 가져와서 fetchUserRankings에서 재사용)
    const [userRanksByType, setUserRanksByType] = useState({ 0: null, 1: null });
    const [organizationContributionLogs, setOrganizationContributionLogs] = useState([]);
    const [organizationTotalPoint, setOrganizationTotalPoint] = useState(0);
    // 랭킹/기여도 데이터 로드 완료 플래그 (effect 의존성을 원시값으로 해 무한루프 방지)
    const [organizationRankingsDataReady, setOrganizationRankingsDataReady] = useState(false);

    useEffect(() => {
        const meInfoString = localStorage.getItem('meInfo');
        if (meInfoString) {
            setMeInfo(JSON.parse(meInfoString));
        }
    }, []);

    // location state에서 업데이트된 organization이 있으면 originOrganization 업데이트 (id만 비교해 참조 변경 시 무한 재실행 방지)
    const locationOrgId = location.state?.organization?.id;
    useEffect(() => {
        if (location.state?.organization) {
            setOriginOrganization(location.state.organization);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- location.state 대신 locationOrgId만 의존
    }, [locationOrgId]);

    // 데이터 로드 (중복 실행 방지용 ref)
    const fetchDetailInProgressRef = React.useRef(false);
    useEffect(() => {
        if (!originOrganization?.id) return;
        if (fetchDetailInProgressRef.current) return;
        fetchDetailInProgressRef.current = true;
        fetchOrganizationDetail().finally(() => {
            fetchDetailInProgressRef.current = false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizationDetail는 일반 함수
    }, [originOrganization?.id]);

    // 랭킹 타입 변경 시 표시만 캐시에서 갱신 (의존성 원시값 사용으로 무한루프 방지)
    useEffect(() => {
        if (originOrganization && organization && originOrganization.memberCount > 0 && organizationMemberships.length > 0 && organizationRankingsDataReady) {
            fetchUserRankings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchUserRankings는 일반 함수
    }, [rankingTypeIndex, originOrganization?.id, organization?.id, organizationMemberships.length, organizationRankingsDataReady]);

    // 기관 랭킹 타입 변경 시 표시값만 캐시에서 갱신 (의존성 원시값 사용으로 무한루프 방지)
    const hasOrgRankingCache = organizationRankingByType[1] !== null;
    useEffect(() => {
        if (originOrganization && hasOrgRankingCache) {
            fetchOrganizationRanking();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizationRanking는 일반 함수
    }, [organizationRankingType, originOrganization?.id, hasOrgRankingCache]);

    // admin이면 사용자 조직 목록 가져오기
    useEffect(() => {
        const loadUserOrganizations = async () => {
            try {
                if (!meInfo) {
                    setLoading(false);
                    return;
                }

                // admin이면 사용자 조직 목록 가져오기
                if (isAdmin(meInfo?.grade) && meInfo.id) {
                    try {
                        // grade 50, 60 모두 membership에서 가져오기
                        const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                        const memberships = membershipsResponse?.data?.memberships || [];
                        const org = memberships.length > 0 ? memberships[0].organization : null;

                        if (org && org.state === 0) {
                            setOriginOrganization(org);
                        } else {
                            const organizationResponse = await organizationAPI.getOrganizations({ userId: meInfo.id });
                            const organization = organizationResponse?.data?.organizations?.[0] || organizationResponse?.organizations?.[0] || null;
                            navigate('/applications/detail', {
                                state: { organization: organization }
                            });
                            return;
                        }
                    } catch (apiErr) {
                        console.error('Failed to fetch user organizations:', apiErr);
                        setLoading(false);
                    }
                } else {
                    setLoading(false);
                }
            } catch (err) {
                console.error('Failed to parse meInfo:', err);
                setLoading(false);
            }
        };

        loadUserOrganizations();
    }, [meInfo, navigate]);

    // 기관 멤버십 100개씩 전부 가져오기 (fetchOrganizationDetail, fetchUserRankings, fetchChartDataForPeriod에서 공용)
    const fetchOrganizationMemberships = async (organizationId) => {
        let list = [];
        let offset = 0;
        while (true) {
            const res = await membershipAPI.getMemberships({
                organizationId,
                type: 1,
                state: 0,
                offset,
            });
            const page = res?.data?.memberships || [];
            if (page.length === 0) return list;
            list = list.concat(page);
            const total = res?.data?.total ?? res?.total ?? 0;
            if (page.length < API_PAGE_SIZE || (total > 0 && offset + page.length >= total)) return list;
            offset += page.length;
        }
    };

    const fetchOrganizationDetail = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!originOrganization) {
                setError(t('organization_information_not_found'));
                setLoading(false);
                return;
            }

            setOrganization(originOrganization);
            setOrganizationMemberships([]);
            setOrganizationRankingByType({ 0: null, 1: null });
            setUserRanksByType({ 0: null, 1: null });
            setOrganizationContributionLogs([]);
            setOrganizationTotalPoint(0);
            setOrganizationRankingsDataReady(false);

            // 각 API를 개별적으로 호출하여 하나가 실패해도 다른 것들은 처리되도록 함
            let memberships = [];
            let didims = [];
            let playLogs = [];
            let ranks = [];
            let users = [];
            let champions = [];

            try {
                memberships = await fetchOrganizationMemberships(originOrganization.id);
                setOrganizationMemberships(memberships);
            } catch (err) {
                console.error('Failed to fetch memberships:', err);
            }

            try {
                didims = [];
                let dOffset = 0;
                while (true) {
                    const didimsRes = await didimAPI.getDidims({ state: 0, expand: 'organization', offset: dOffset });
                    const page = didimsRes?.data?.didims || didimsRes?.didims || [];
                    if (page.length === 0) break;
                    didims = didims.concat(page);
                    const total = didimsRes?.data?.total ?? didimsRes?.total ?? 0;
                    if (page.length < API_PAGE_SIZE || (total > 0 && dOffset + page.length >= total)) break;
                    dOffset += page.length;
                }
            } catch (err) {
                console.error('Failed to fetch didims:', err);
            }

            try {
                playLogs = [];
                let plOffset = 0;
                while (true) {
                    const playLogsRes = await logAPI.getPlayLogs({ startTime: getTodayStartTime(), offset: plOffset });
                    const page = playLogsRes?.data?.playLogs || playLogsRes?.playLogs || [];
                    if (page.length === 0) break;
                    playLogs = playLogs.concat(page);
                    const total = playLogsRes?.data?.total ?? playLogsRes?.total ?? 0;
                    if (page.length < API_PAGE_SIZE || (total > 0 && plOffset + page.length >= total)) break;
                    plOffset += page.length;
                }
            } catch (err) {
                console.error('Failed to fetch play logs:', err);
            }

            try {
                ranks = [];
                let rOffset = 0;
                while (true) {
                    const ranksRes = await rankAPI.getOrganizationRanks({ type: 1, organizationId: originOrganization.id, offset: rOffset });
                    const page = ranksRes?.data?.ranks || ranksRes?.ranks || [];
                    if (page.length === 0) break;
                    ranks = ranks.concat(page);
                    const total = ranksRes?.data?.total ?? ranksRes?.total ?? 0;
                    if (page.length < API_PAGE_SIZE || (total > 0 && rOffset + page.length >= total)) break;
                    rOffset += page.length;
                }
                const ranking1 = ranks?.length > 0 ? (ranks[0]?.rank ?? null) : null;
                setOrganizationRankingByType(prev => ({ ...prev, 1: ranking1 }));
                let ranks0 = [];
                rOffset = 0;
                while (true) {
                    const ranksRes0 = await rankAPI.getOrganizationRanks({ type: 0, organizationId: originOrganization.id, offset: rOffset });
                    const page0 = ranksRes0?.data?.ranks || ranksRes0?.ranks || [];
                    if (page0.length === 0) break;
                    ranks0 = ranks0.concat(page0);
                    const total0 = ranksRes0?.data?.total ?? ranksRes0?.total ?? 0;
                    if (page0.length < API_PAGE_SIZE || (total0 > 0 && rOffset + page0.length >= total0)) break;
                    rOffset += page0.length;
                }
                const ranking0 = ranks0?.length > 0 ? (ranks0[0]?.rank ?? null) : null;
                setOrganizationRankingByType(prev => ({ ...prev, 0: ranking0 }));
            } catch (err) {
                console.error('Failed to fetch ranks:', err);
            }

            try {
                users = [];
                let uOffset = 0;
                while (true) {
                    const usersRes = await userAPI.getUsers('all', { state: 0, offset: uOffset });
                    const page = usersRes?.data?.users || usersRes?.users || [];
                    if (page.length === 0) break;
                    users = users.concat(page);
                    const total = usersRes?.data?.total ?? usersRes?.total ?? 0;
                    if (page.length < API_PAGE_SIZE || (total > 0 && uOffset + page.length >= total)) break;
                    uOffset += page.length;
                }
                setAllUsers(users);
            } catch (err) {
                console.error('Failed to fetch users:', err);
            }

            try {
                champions = [];
                let cOffset = 0;
                while (true) {
                    const championsRes = await championAPI.getChampions({ organizationId: originOrganization.id, state: '0,2', offset: cOffset });
                    const page = championsRes?.data?.champions || championsRes?.champions || [];
                    if (page.length === 0) break;
                    champions = champions.concat(page);
                    const total = championsRes?.data?.total ?? championsRes?.total ?? 0;
                    if (page.length < API_PAGE_SIZE || (total > 0 && cOffset + page.length >= total)) break;
                    cOffset += page.length;
                }
            } catch (err) {
                console.error('Failed to fetch champions:', err);
            }

            // 멤버십 데이터 처리
            const orgMemberships = memberships.filter(m => m.organizationId === originOrganization.id);
            const orgUserIds = orgMemberships.map(m => m.userId);
            const orgUsers = users.filter(u => orgUserIds.includes(u.id));

            // 최신 didims 배열에서 해당 기관에 연결된 디딤 id 추출
            const orgDidims = didims.filter(d => d.organizationId === originOrganization.id);
            const didimIds = orgDidims
                .map(d => d.id)
                .filter(id => id != null)
                .sort((a, b) => a - b);

            // 금일 사용 로그
            const orgPlayLogs = playLogs.filter(log =>
                log.organizationId === originOrganization.id
            );

            // 역대 최대 일일 사용시간 계산을 위한 전체 플레이 로그 가져오기 (100개씩 배치)
            let maxDailyUsageMinutes = 0;
            try {
                let allPlayLogs = [];
                let plOffset = 0;
                while (true) {
                    const allPlayLogsRes = await logAPI.getPlayLogs({
                        organizationId: originOrganization.id,
                        offset: plOffset,
                    });
                    const page = allPlayLogsRes?.data?.playLogs || [];
                    if (page.length === 0) break;
                    allPlayLogs = allPlayLogs.concat(page);
                    const total = allPlayLogsRes?.data?.total ?? allPlayLogsRes?.total ?? 0;
                    if (page.length < API_PAGE_SIZE || (total > 0 && plOffset + page.length >= total)) break;
                    plOffset += page.length;
                }

                // 일별로 그룹화하여 최대값 찾기
                const dailyUsage = {};
                allPlayLogs.forEach(log => {
                    if (log.startTime && log.endTime) {
                        const date = new Date(log.startTime);
                        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        const start = new Date(log.startTime);
                        const end = new Date(log.endTime);
                        const minutes = (end - start) / (1000 * 60);

                        if (!dailyUsage[dateKey]) {
                            dailyUsage[dateKey] = 0;
                        }
                        dailyUsage[dateKey] += minutes;
                    }
                });

                maxDailyUsageMinutes = Math.max(...Object.values(dailyUsage), 0);
            } catch (err) {
                console.error('Failed to fetch all play logs for max daily usage:', err);
                // 실패하면 오늘 사용시간을 기본값으로 사용
                maxDailyUsageMinutes = 750;
            }

            // 금일 통계 계산
            const todayParticipants = new Set(orgPlayLogs.map(log => log.userId)).size;
            const todayTotalUsageMinutes = orgPlayLogs.reduce((total, log) => {
                if (log.startTime && log.endTime) {
                    const start = new Date(log.startTime);
                    const end = new Date(log.endTime);
                    return total + (end - start) / (1000 * 60);
                }
                return total;
            }, 0);

            // physicalRecord에서 평균 걸음수 계산 (1보 이상인 사람들의 평균)
            let todayAvgSteps = 0;
            try {
                // 오늘 날짜 기준으로 physicalRecord 가져오기
                const today = new Date();
                const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD 형식

                // userId를 콤마로 연결하여 한 번에 조회
                if (orgUserIds.length > 0) {
                    const userIdsStr = orgUserIds.join(',');
                    const physicalRecordResponse = await physicalRecordAPI.getPhysicalRecords({
                        userId: userIdsStr,
                        state: 0,
                        startDate: todayStr,
                        endDate: todayStr,
                        limit: orgUserIds.length,
                    });

                    const records = physicalRecordResponse?.data?.physicalRecords || [];

                    // 오늘 날짜의 physicalRecord 중 totalSteps가 1 이상인 것만 필터링
                    const validSteps = records
                        .filter(record => {
                            if (!record.date) return false;
                            const recordDate = new Date(record.date);
                            const recordDateStr = recordDate.toISOString().split('T')[0];
                            return recordDateStr === todayStr && record.totalSteps >= 1;
                        })
                        .map(record => record.totalSteps);

                    // 1보 이상인 사람들의 평균 계산
                    if (validSteps.length > 0) {
                        todayAvgSteps = Math.round(validSteps.reduce((sum, steps) => sum + steps, 0) / validSteps.length);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch physical records for average steps:', err);
                todayAvgSteps = 0;
            }

            // 랭킹 데이터 (기본은 누적활동랭킹, 캐시에서 표시값 설정)
            const ranking = ranks?.length > 0 ? (ranks[0]?.rank || []) : [];
            setOrganizationRanking(ranking);

            // 활성 챔피언십 수
            // 현재 날짜를 ISO 형식으로 변환 
            const now = new Date();
            const currentDateISO = now.toISOString();

            const activeChampions = champions.filter(c => c.endDate >= currentDateISO || c.endDate === null);
            const activeChallenges = activeChampions.length;

            // 사용자 랭킹 가져오기 (누적·이달 둘 다 로드 후 캐시)
            const [ranksCumulativeRes, ranksMonthlyRes] = await Promise.all([
                rankAPI.getRanks({ organizationId: originOrganization.id, limit: orgUserIds.length }),
                (() => {
                    const now = new Date();
                    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    return rankAPI.getMonthlyRanks({ organizationId: originOrganization.id, month, limit: orgUserIds.length });
                })(),
            ]);
            const userRanksCumulative = ranksCumulativeRes?.data?.ranks || ranksCumulativeRes?.ranks || [];
            const userRanksMonthly = ranksMonthlyRes?.data?.ranks || ranksMonthlyRes?.ranks || [];
            setUserRanksByType({ 0: userRanksCumulative, 1: userRanksMonthly });
            const userRanks = rankingTypeIndex === 0 ? userRanksCumulative : userRanksMonthly;

            // 기관 포인트 기여도 로그 가져오기 (경험치와 기여도 계산용, 100개씩 배치)
            let contributionLogs = [];
            let totalPoint = 0;
            let clOffset = 0;
            while (true) {
                const contributionLogsRes = await logAPI.getOrganizationPointContributionLogs({
                    organizationId: originOrganization.id,
                    offset: clOffset,
                });
                const page = contributionLogsRes?.data?.logs || contributionLogsRes?.logs || [];
                if (totalPoint === 0) totalPoint = contributionLogsRes?.data?.totalPoint ?? contributionLogsRes?.totalPoint ?? 0;
                if (page.length === 0) break;
                contributionLogs = contributionLogs.concat(page);
                const total = contributionLogsRes?.data?.total ?? contributionLogsRes?.total ?? 0;
                if (page.length < API_PAGE_SIZE || (total > 0 && clOffset + page.length >= total)) break;
                clOffset += page.length;
            }
            setOrganizationContributionLogs(contributionLogs);
            setOrganizationTotalPoint(totalPoint);
            setOrganizationRankingsDataReady(true);

            // 멤버십 정보를 userId로 매핑 (세부소속 정보 가져오기용)
            const membershipMap = new Map();
            orgMemberships.forEach(membership => {
                membershipMap.set(membership.userId, {
                    division: membership.division || '-',
                    section: membership.section || '-',
                });
            });

            // 랭킹 데이터를 userId로 매핑
            const rankMap = new Map();
            userRanks.forEach((r, idx) => {
                const userId = r.userId || r.id;
                if (userId != null && orgUserIds.includes(userId)) {
                    const rankValue = r.ranking != null ? r.ranking : (idx + 1);
                    rankMap.set(userId, rankValue);
                }
            });

            // 기여도 데이터를 userId로 매핑
            const contributionMap = new Map();
            contributionLogs
                .filter(log => log.userId && orgUserIds.includes(log.userId))
                .forEach(log => {
                    const contribution = totalPoint > 0 ? ((log.point || 0) / totalPoint * 100) : 0;
                    contributionMap.set(log.userId, {
                        point: log.point !== undefined && log.point !== null ? log.point : 0,
                        contribution: contribution,
                    });
                });

            // 모든 사용자를 포함하되, 랭킹 데이터가 없는 사용자는 경험치와 기여도를 비워둠
            const usersWithRankData = orgUserIds.map(userId => {
                const user = orgUsers.find(u => u.id === userId);
                const membership = membershipMap.get(userId);
                const rank = rankMap.get(userId);
                const contributionData = contributionMap.get(userId);

                return {
                    userId: userId, // userId 추가
                    rank: rank !== undefined && rank !== null ? rank : '-',
                    userName: user?.name || t('user'),
                    division: membership?.division || '-',
                    section: membership?.section || '-',
                    point: contributionData?.point !== undefined && contributionData?.point !== null ? contributionData.point : null,
                    contribution: contributionData?.contribution !== undefined && contributionData?.contribution !== null ? contributionData.contribution : null,
                };
            }).sort((a, b) => {
                // 랭킹순으로 정렬 (숫자는 오름차순, '-'는 맨 뒤로)
                if (a.rank === '-') return 1;
                if (b.rank === '-') return -1;
                return (a.rank || 0) - (b.rank || 0);
            });

            setUserRankings(usersWithRankData);

            // 통계 업데이트
            setStats({
                didimIds: didimIds.join(','),
                ranking,
                activeChallenges,
                todayParticipants,
                todayTotalUsage: Math.round(todayTotalUsageMinutes),
                todayAvgSteps,
            });

            // 레벨 경험치 계산 (organizationLevels에서 현재 레벨의 min/max 찾기)
            const currentLevel = originOrganization?.level || 1;
            const currentExp = originOrganization?.exp || 0;
            let levelMinExp = 0;
            let levelMaxExp = 10000;

            // organizationLevels는 context에서 이미 파싱된 배열 형태로 제공됨
            // organizationLevels가 null이거나 아직 로딩 중이면 기본값 사용
            const levels = organizationLevels && Array.isArray(organizationLevels) && !levelsLoading ? organizationLevels : null;

            if (levels && Array.isArray(levels) && levels.length > 0) {
                const currentLevelData = levels.find(l => l.level === currentLevel);
                const prevLevelData = levels.find(l => l.level === currentLevel - 1);

                if (currentLevelData) {
                    // min: 이전 레벨의 totalExp (현재 레벨 시작점)
                    // 레벨 1인 경우 이전 레벨이 없으므로 0
                    levelMinExp = prevLevelData?.totalExp || 0;

                    // max: 현재 레벨의 totalExp (다음 레벨 시작점 = 현재 레벨 목표)
                    levelMaxExp = currentLevelData.totalExp || (levelMinExp + 10000);
                } else {
                    // 현재 레벨 데이터를 찾지 못한 경우 기본값 사용
                    levelMinExp = currentExp;
                    levelMaxExp = currentExp + 10000;
                }
            } else {
                // organizationLevels가 없거나 로딩 중일 경우 기본값 사용
                levelMinExp = currentExp;
                levelMaxExp = currentExp + 10000;
            }

            // 걸음수 max 계산 (현재값이 10000보다 많으면 그 값을 max로)
            const stepsMax = Math.max(10000, todayAvgSteps);

            // 진행 바 업데이트
            setProgressBars([
                {
                    current: currentExp,
                    min: levelMinExp,
                    target: levelMaxExp,
                    label: t('exp'),
                    unit: '',
                    display: `${currentExp.toLocaleString()}`
                },
                {
                    current: todayParticipants,
                    min: 0,
                    target: originOrganization.memberCount || 1,
                    label: t('org_detail_participants'),
                    unit: '',
                    display: `${todayParticipants}`
                },
                {
                    current: Math.round(todayTotalUsageMinutes),
                    min: 0,
                    target: Math.max(Math.round(maxDailyUsageMinutes), Math.round(todayTotalUsageMinutes)) || 60,
                    label: t('time'),
                    unit: 'm',
                    display: formatTime(Math.round(todayTotalUsageMinutes))
                },
                {
                    current: todayAvgSteps,
                    min: 0,
                    target: stepsMax,
                    label: t('org_detail_steps'),
                    unit: t('steps'),
                    display: `${todayAvgSteps.toLocaleString()}${t('steps')}`
                },
            ]);

        } catch (err) {
            console.error('Failed to fetch organization detail:', err);
            setError(t('failed_to_load_organization_information'));
        } finally {
            setLoading(false);
        }
    };

    const fetchUserRankings = () => {
        if (!originOrganization || !organizationMemberships?.length) return;
        const userRanks = userRanksByType[rankingTypeIndex];
        if (userRanks == null) return;

        const contributionLogs = organizationContributionLogs;
        const totalPoint = organizationTotalPoint;
        const orgMemberships = organizationMemberships.filter(m => m.organizationId === originOrganization.id);
        const orgUserIds = orgMemberships.map(m => m.userId);

        // 멤버십 정보를 userId로 매핑 (세부소속 정보 가져오기용)
        const membershipMap = new Map();
        orgMemberships.forEach(membership => {
            membershipMap.set(membership.userId, {
                division: membership.division || '-',
                section: membership.section || '-',
            });
        });

        // 사용자 정보 가져오기 (이미 가져온 allUsers 사용)
        const orgUsers = allUsers.filter(u => orgUserIds.includes(u.id));

        // 랭킹 데이터를 userId로 매핑
        const rankMap = new Map();
        userRanks.forEach((r, idx) => {
            const userId = r.userId || r.id;
            if (userId != null && orgUserIds.includes(userId)) {
                const rankValue = r.ranking != null ? r.ranking : (idx + 1);
                rankMap.set(userId, rankValue);
            }
        });

        // 기여도 데이터를 userId로 매핑
        const contributionMap = new Map();
        contributionLogs
            .filter(log => log.userId && orgUserIds.includes(log.userId))
            .forEach(log => {
                const contribution = totalPoint > 0 ? ((log.point || 0) / totalPoint * 100) : 0;
                contributionMap.set(log.userId, {
                    point: log.point !== undefined && log.point !== null ? log.point : 0,
                    contribution: contribution,
                });
            });

        // 모든 사용자를 포함하되, 랭킹 데이터가 없는 사용자는 경험치와 기여도를 비워둠
        const usersWithRankData = orgUserIds.map(userId => {
            const user = orgUsers.find(u => u.id === userId);
            const membership = membershipMap.get(userId);
            const rank = rankMap.get(userId);
            const contributionData = contributionMap.get(userId);

            return {
                userId: userId, // userId 추가
                rank: rank !== undefined && rank !== null ? rank : '-',
                userName: user?.name || t('user'),
                division: membership?.division || '-',
                section: membership?.section || '-',
                point: contributionData?.point !== undefined && contributionData?.point !== null ? contributionData.point : null,
                contribution: contributionData?.contribution !== undefined && contributionData?.contribution !== null ? contributionData.contribution : null,
            };
        }).sort((a, b) => {
            // 랭킹순으로 정렬 (숫자는 오름차순, '-'는 맨 뒤로)
            if (a.rank === '-') return 1;
            if (b.rank === '-') return -1;
            return (a.rank || 0) - (b.rank || 0);
        });

        setUserRankings(usersWithRankData);
    };

    const fetchOrganizationRanking = () => {
        if (!originOrganization) return;
        const ranking = organizationRankingByType[organizationRankingType] ?? null;
        setOrganizationRanking(ranking);
        setStats(prev => ({
            ...prev,
            ranking
        }));
    };

    const loadChartData = async () => {
        if (!originOrganization?.id) return;

        try {
            // 선택된 기간의 시작일과 종료일 계산
            const year = chartCurrentDate.getFullYear();
            const month = chartCurrentDate.getMonth();

            let startDate, endDate;
            if (chartPeriod === 'monthly') {
                // 월간: 해당 월의 첫날과 마지막날
                startDate = new Date(year, month, 1);
                endDate = new Date(year, month + 1, 0, 23, 59, 59);

                // 이전 달 (비교용)
                const prevMonth = new Date(year, month - 1, 1);
                const prevMonthEnd = new Date(year, month, 0, 23, 59, 59);

                await fetchChartDataForPeriod(startDate, endDate, prevMonth, prevMonthEnd);
            } else {
                // 연간: 해당 연도의 첫날과 마지막날
                startDate = new Date(year, 0, 1);
                endDate = new Date(year, 11, 31, 23, 59, 59);

                // 이전 연도 (비교용)
                const prevYear = new Date(year - 1, 0, 1);
                const prevYearEnd = new Date(year - 1, 11, 31, 23, 59, 59);

                await fetchChartDataForPeriod(startDate, endDate, prevYear, prevYearEnd);
            }
        } catch (err) {
            console.error('Failed to load chart data:', err);
            setChartData([]);
        }
    };

    // 차트 데이터 로드 (월/년 변경 시, 의존성 원시값 사용으로 무한루프 방지)
    useEffect(() => {
        if (!originOrganization?.id || !organizationMemberships?.length) return;
        loadChartData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadChartData 제외로 무한루프 방지
    }, [chartCurrentDate, chartPeriod, chartTab, originOrganization?.id, organizationMemberships.length]);

    const fetchChartDataForPeriod = async (currentStart, currentEnd, previousStart, previousEnd) => {
        if (!originOrganization?.id || !organizationMemberships?.length) return;

        try {
            // 현재 기간의 year와 month 계산
            const year = chartCurrentDate.getFullYear();
            const month = chartCurrentDate.getMonth();

            // 멤버십 및 사용자 목록 (state에서 재사용)
            const orgMemberships = organizationMemberships.filter(m => m.organizationId === originOrganization.id);
            const orgUserIds = orgMemberships.map(m => m.userId);

            // 플레이 로그 100건씩 배치 조회 (백엔드 기본 limit 100, offset만 전달)
            const fetchPlayLogsBatched = async (organizationId, startTime, endTime) => {
                let offset = 0;
                let all = [];
                let page;
                do {
                    const res = await logAPI.getPlayLogs({
                        organizationId,
                        startTime,
                        endTime,
                        offset
                    }).catch(() => ({ data: { logs: [] } }));
                    page = res?.data?.logs || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };
            // 포인트 로그 100건씩 배치 조회 (백엔드 기본 limit 100, offset만 전달)
            const fetchPointLogsBatched = async (organizationId, startTime, endTime) => {
                let offset = 0;
                let all = [];
                let page;
                do {
                    const res = await logAPI.getOrganizationPointLogs({
                        organizationId,
                        startTime,
                        endTime,
                        offset
                    }).catch(() => ({ data: { logs: [] } }));
                    page = res?.data?.logs || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };

            const [currentPlayLogs, previousPlayLogs, currentPointLogs, previousPointLogs] = await Promise.all([
                fetchPlayLogsBatched(originOrganization.id, currentStart.toISOString(), currentEnd.toISOString()),
                fetchPlayLogsBatched(originOrganization.id, previousStart.toISOString(), previousEnd.toISOString()),
                fetchPointLogsBatched(originOrganization.id, currentStart.toISOString(), currentEnd.toISOString()),
                fetchPointLogsBatched(originOrganization.id, previousStart.toISOString(), previousEnd.toISOString())
            ]);

            // physicalRecord 가져오기 (평균발걸음수 차트용, 100건씩 배치)
            let currentPhysicalRecords = [];
            let previousPhysicalRecords = [];
            if (chartTab === 'steps' && orgUserIds.length > 0) {
                try {
                    const userIdsStr = orgUserIds.join(',');
                    const fetchPhysicalRecordsBatched = async (startDate, endDate) => {
                        let offset = 0;
                        let all = [];
                        let page;
                        do {
                            const res = await physicalRecordAPI.getPhysicalRecords({
                                userId: userIdsStr,
                                state: 0,
                                startDate,
                                endDate,
                                offset
                            }).catch(() => ({ data: { physicalRecords: [] } }));
                            page = res?.data?.physicalRecords || [];
                            all = all.concat(page);
                            offset += API_PAGE_SIZE;
                        } while (page.length === API_PAGE_SIZE);
                        return all;
                    };
                    const [currentPhysicalRecordsFetched, previousPhysicalRecordsFetched] = await Promise.all([
                        fetchPhysicalRecordsBatched(currentStart.toISOString().split('T')[0], currentEnd.toISOString().split('T')[0]),
                        fetchPhysicalRecordsBatched(previousStart.toISOString().split('T')[0], previousEnd.toISOString().split('T')[0])
                    ]);
                    currentPhysicalRecords = currentPhysicalRecordsFetched;
                    previousPhysicalRecords = previousPhysicalRecordsFetched;
                } catch (err) {
                    console.error('Failed to fetch physical records for chart:', err);
                }
            }

            // 차트 데이터 생성
            let chartData = [];

            if (chartPeriod === 'monthly') {
                // 월간: 일별 데이터
                const daysInMonth = new Date(chartCurrentDate.getFullYear(), chartCurrentDate.getMonth() + 1, 0).getDate();
                const daysInPrevMonth = new Date(previousStart.getFullYear(), previousStart.getMonth() + 1, 0).getDate();

                chartData = [];
                for (let day = 1; day <= daysInMonth; day++) {
                    void new Date(year, month, day);
                    void `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

                    let currentValue = 0;
                    let previousValue = 0;

                    if (chartTab === 'participants') {
                        // 참가자수: 해당 일에 활동한 사용자 수
                        const currentDayLogs = currentPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);

                            return logDate.getDate() === day && logDate.getMonth() === month && logDate.getFullYear() === year;
                        });

                        currentValue = new Set(currentDayLogs.map(log => log.userId)).size;

                        const prevDay = Math.min(day, daysInPrevMonth);
                        const prevDayLogs = previousPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);
                            return logDate.getDate() === prevDay && logDate.getMonth() === previousStart.getMonth() && logDate.getFullYear() === previousStart.getFullYear();
                        });
                        previousValue = new Set(prevDayLogs.map(log => log.userId)).size;
                    } else if (chartTab === 'usage') {
                        // 디딤총사용시간: 해당 일의 총 사용 시간 (분)
                        const currentDayLogs = currentPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);
                            return logDate.getDate() === day && logDate.getMonth() === month && logDate.getFullYear() === year;
                        });
                        currentValue = currentDayLogs.reduce((total, log) => {
                            if (log.startTime && log.endTime) {
                                const start = new Date(log.startTime);
                                const end = new Date(log.endTime);
                                return total + (end - start) / (1000 * 60);
                            }
                            return total;
                        }, 0);

                        const prevDay = Math.min(day, daysInPrevMonth);
                        const prevDayLogs = previousPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);
                            return logDate.getDate() === prevDay && logDate.getMonth() === previousStart.getMonth() && logDate.getFullYear() === previousStart.getFullYear();
                        });
                        previousValue = prevDayLogs.reduce((total, log) => {
                            if (log.startTime && log.endTime) {
                                const start = new Date(log.startTime);
                                const end = new Date(log.endTime);
                                return total + (end - start) / (1000 * 60);
                            }
                            return total;
                        }, 0);
                    } else if (chartTab === 'steps') {
                        // 평균발걸음수: 해당 일의 physicalRecord에서 1보 이상인 사람들의 평균
                        const currentDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const currentDayRecords = currentPhysicalRecords.filter(record => {
                            if (!record.date) return false;
                            const recordDate = new Date(record.date);
                            const recordDateStr = recordDate.toISOString().split('T')[0];
                            return recordDateStr === currentDateStr && record.totalSteps >= 1;
                        });
                        const currentDayStepValues = currentDayRecords.map(record => record.totalSteps);
                        currentValue = currentDayStepValues.length > 0
                            ? Math.round(currentDayStepValues.reduce((sum, steps) => sum + steps, 0) / currentDayStepValues.length)
                            : 0;



                        const prevDay = Math.min(day, daysInPrevMonth);
                        const prevDateStr = `${previousStart.getFullYear()}-${String(previousStart.getMonth() + 1).padStart(2, '0')}-${String(prevDay).padStart(2, '0')}`;
                        const prevDayRecords = previousPhysicalRecords.filter(record => {
                            if (!record.date) return false;
                            const recordDate = new Date(record.date);
                            const recordDateStr = recordDate.toISOString().split('T')[0];
                            return recordDateStr === prevDateStr && record.totalSteps >= 1;
                        });
                        const prevDayStepValues = prevDayRecords.map(record => record.totalSteps);
                        previousValue = prevDayStepValues.length > 0
                            ? Math.round(prevDayStepValues.reduce((sum, steps) => sum + steps, 0) / prevDayStepValues.length)
                            : 0;
                    } else if (chartTab === 'points') {
                        // 획득기관포인트: 해당 일에 획득한 포인트 합계
                        const currentDayLogs = currentPointLogs.filter(log => {
                            if (!log.time) return false;
                            const logDate = new Date(log.time);
                            return logDate.getDate() === day && logDate.getMonth() === month && logDate.getFullYear() === year && (log.type === 0 || log.point > 0);
                        });
                        currentValue = currentDayLogs.reduce((total, log) => total + (log.point || 0), 0);

                        const prevDay = Math.min(day, daysInPrevMonth);
                        const prevDayLogs = previousPointLogs.filter(log => {
                            if (!log.time) return false;
                            const logDate = new Date(log.time);
                            return logDate.getDate() === prevDay && logDate.getMonth() === previousStart.getMonth() && logDate.getFullYear() === previousStart.getFullYear() && (log.type === 0 || log.point > 0);
                        });
                        previousValue = prevDayLogs.reduce((total, log) => total + (log.point || 0), 0);
                    }

                    chartData.push({
                        day: day,
                        current: Math.round(currentValue),
                        previous: Math.round(previousValue),
                    });
                }
            } else {
                // 연간: 월별 데이터
                chartData = [];
                for (let m = 0; m < 12; m++) {
                    let currentValue = 0;
                    let previousValue = 0;

                    if (chartTab === 'participants') {
                        // 참가자수: 해당 월에 활동한 사용자 수
                        const currentMonthLogs = currentPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);
                            return logDate.getMonth() === m && logDate.getFullYear() === year;
                        });
                        currentValue = new Set(currentMonthLogs.map(log => log.userId)).size;

                        const prevMonthLogs = previousPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);
                            return logDate.getMonth() === m && logDate.getFullYear() === year - 1;
                        });
                        previousValue = new Set(prevMonthLogs.map(log => log.userId)).size;
                    } else if (chartTab === 'usage') {
                        // 디딤총사용시간: 해당 월의 총 사용 시간 (분)
                        const currentMonthLogs = currentPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);
                            return logDate.getMonth() === m && logDate.getFullYear() === year;
                        });
                        currentValue = currentMonthLogs.reduce((total, log) => {
                            if (log.startTime && log.endTime) {
                                const start = new Date(log.startTime);
                                const end = new Date(log.endTime);
                                return total + (end - start) / (1000 * 60);
                            }
                            return total;
                        }, 0);

                        const prevMonthLogs = previousPlayLogs.filter(log => {
                            if (!log.startTime) return false;
                            const logDate = new Date(log.startTime);
                            return logDate.getMonth() === m && logDate.getFullYear() === year - 1;
                        });
                        previousValue = prevMonthLogs.reduce((total, log) => {
                            if (log.startTime && log.endTime) {
                                const start = new Date(log.startTime);
                                const end = new Date(log.endTime);
                                return total + (end - start) / (1000 * 60);
                            }
                            return total;
                        }, 0);
                    } else if (chartTab === 'steps') {
                        // 평균발걸음수: 해당 월의 physicalRecord에서 1보 이상인 사람들의 평균
                        const currentMonthStr = `${year}-${String(m + 1).padStart(2, '0')}`;
                        const currentMonthRecords = currentPhysicalRecords.filter(record => {
                            if (!record.date) return false;
                            const recordDate = new Date(record.date);
                            const recordMonthStr = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}`;
                            return recordMonthStr === currentMonthStr && record.totalSteps >= 1;
                        });

                        const currentMonthStepValues = currentMonthRecords.map(record => record.totalSteps);
                        currentValue = currentMonthStepValues.length > 0
                            ? Math.round(currentMonthStepValues.reduce((sum, steps) => sum + steps, 0) / currentMonthStepValues.length)
                            : 0;

                        const prevMonthStr = `${year - 1}-${String(m + 1).padStart(2, '0')}`;
                        const prevMonthRecords = previousPhysicalRecords.filter(record => {
                            if (!record.date) return false;
                            const recordDate = new Date(record.date);
                            const recordMonthStr = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}`;
                            return recordMonthStr === prevMonthStr && record.totalSteps >= 1;
                        });
                        const prevMonthStepValues = prevMonthRecords.map(record => record.totalSteps);
                        previousValue = prevMonthStepValues.length > 0
                            ? Math.round(prevMonthStepValues.reduce((sum, steps) => sum + steps, 0) / prevMonthStepValues.length)
                            : 0;
                    } else if (chartTab === 'points') {
                        // 획득기관포인트: 해당 월에 획득한 포인트 합계
                        const currentMonthLogs = currentPointLogs.filter(log => {
                            if (!log.time) return false;
                            const logDate = new Date(log.time);
                            return logDate.getMonth() === m && logDate.getFullYear() === year && (log.type === 0 || log.point > 0);
                        });
                        currentValue = currentMonthLogs.reduce((total, log) => total + (log.point || 0), 0);

                        const prevMonthLogs = previousPointLogs.filter(log => {
                            if (!log.time) return false;
                            const logDate = new Date(log.time);
                            return logDate.getMonth() === m && logDate.getFullYear() === year - 1 && (log.type === 0 || log.point > 0);
                        });
                        previousValue = prevMonthLogs.reduce((total, log) => total + (log.point || 0), 0);
                    }

                    chartData.push({
                        day: m + 1,
                        current: Math.round(currentValue),
                        previous: Math.round(previousValue),
                    });
                }
            }

            setChartData(chartData);
        } catch (err) {
            console.error('Failed to fetch chart data:', err);
            setChartData([]);
        }
    };

    const handleEdit = () => {
        if (organization) {
            navigate('/organizations/edit', {
                state: { organization }
            });
        }
    };

    // 첫 번째 드롭다운 옵션 (전체 / 세부소속1 / 세부소속2)
    const getDivisionTypeOptions = () => [
        { value: 'all', label: 'all' },
        { value: 'division_1', label: 'division_1' },
        { value: 'division_2', label: 'division_2' },
    ];

    // 두 번째 드롭다운 옵션 (세부소속1 이름들)
    const getDivision1Options = () => {
        if (!organization?.divisionSections || (selectedDivisionType !== 'division_1' && selectedDivisionType !== 'division_2')) {
            return [];
        }

        const divisions = typeof organization.divisionSections === 'string'
            ? JSON.parse(organization.divisionSections)
            : organization.divisionSections;

        const options = [{ value: 'all', label: t('all') }];
        Object.keys(divisions).forEach(div => {
            options.push({ value: div, label: div });
        });
        return options;
    };

    // 세 번째 드롭다운 옵션 (세부소속2 이름들)
    const getDivision2Options = () => {
        if (!organization?.divisionSections || selectedDivisionType !== 'division_2' || !selectedDivision1 || selectedDivision1 === 'all') {
            return [];
        }

        const divisions = typeof organization.divisionSections === 'string'
            ? JSON.parse(organization.divisionSections)
            : organization.divisionSections;

        const sections = divisions[selectedDivision1];
        if (!sections || !Array.isArray(sections)) {
            return [];
        }

        const options = [{ value: 'all', label: t('all') }];
        sections.forEach(section => {
            options.push({ value: section, label: section });
        });
        return options;
    };

    // 필터링된 사용자 랭킹
    const filteredUserRankings = useMemo(() => {
        let filtered = [...userRankings];

        if (selectedDivisionType === 'all') {
            return filtered;
        }

        if (selectedDivisionType === 'division_1') {
            if (selectedDivision1 && selectedDivision1 !== 'all') {
                filtered = filtered.filter(user => user.division === selectedDivision1);
            }
        } else if (selectedDivisionType === 'division_2') {
            if (selectedDivision1 && selectedDivision1 !== 'all') {
                filtered = filtered.filter(user => user.division === selectedDivision1);
                if (selectedDivision2 && selectedDivision2 !== 'all') {
                    filtered = filtered.filter(user => user.section === selectedDivision2);
                }
            }
        }

        return filtered;
    }, [userRankings, selectedDivisionType, selectedDivision1, selectedDivision2]);

    const formatDivisionSections = () => {
        if (!organization?.divisionSections) return { div1: '0', div2: '0' };

        const divisions = typeof organization.divisionSections === 'string'
            ? JSON.parse(organization.divisionSections)
            : organization.divisionSections;

        const div1 = Object.keys(divisions).length.toString();
        const div2 = Object.values(divisions).map(arr => Array.isArray(arr) ? arr.length : 0).join(',');
        return { div1, div2 };
    };

    // 경험치 상세보기 팝업 관련 함수들
    const handleOpenExpDetail = () => {
        setExpDetailOpen(true);
    };

    const handleCloseExpDetail = () => {
        setExpDetailOpen(false);
    };

    const loadMonthlyExpLogs = async () => {
        if (!originOrganization?.id) return;

        try {
            const year = expCurrentDate.getFullYear();
            const month = expCurrentDate.getMonth();
            const startDate = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0, 23, 59, 59);

            let allLogs = [];
            let offset = 0;
            let page;
            do {
                const res = await logAPI.getOrganizationPointLogs({
                    organizationId: originOrganization.id,
                    startTime: startDate.toISOString(),
                    endTime: endDate.toISOString(),
                    offset
                });
                page = res?.data?.logs || [];
                allLogs = allLogs.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allLogs.length > 0) {
                // type이 0인 것만 필터링 (경험치 로그)
                const expLogs = allLogs.filter(log => log.type === 0);
                setExpPointLogData(expLogs);
            } else {
                setExpPointLogData([]);
            }
        } catch (error) {
            console.error('Failed to load organization point log data:', error);
            setExpPointLogData([]);
        }
    };

    useEffect(() => {
        if (expDetailOpen && originOrganization?.id) {
            loadMonthlyExpLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadMonthlyExpLogs 제외로 무한루프 방지
    }, [expCurrentDate, expDetailOpen, originOrganization?.id]);

    // 포인트 상세보기 팝업 관련 함수들
    const handleOpenPointDetail = () => {
        setPointDetailOpen(true);
        loadPointLogsByPeriod();
    };

    const handleClosePointDetail = () => {
        setPointDetailOpen(false);
    };

    const loadPointLogsByPeriod = async () => {
        if (!originOrganization?.id) return;

        try {
            const now = new Date();
            let startDate;

            // 기간 필터에 따라 시작일 계산
            if (pointPeriod === 7) {
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            } else if (pointPeriod === 30) {
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            } else {
                // 전체 기간
                startDate = new Date(0);
            }

            let allLogs = [];
            let offset = 0;
            let page;
            do {
                const res = await logAPI.getOrganizationPointLogs({
                    organizationId: originOrganization.id,
                    startTime: startDate.toISOString(),
                    endTime: now.toISOString(),
                    offset
                });
                page = res?.data?.logs || [];
                allLogs = allLogs.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allLogs.length > 0) {
                setPointLogData(allLogs);
            } else {
                setPointLogData([]);
            }
        } catch (error) {
            console.error('Failed to load point log data:', error);
            setPointLogData([]);
        }
    };

    useEffect(() => {
        if (pointDetailOpen && originOrganization?.id) {
            loadPointLogsByPeriod();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPointLogsByPeriod 제외로 무한루프 방지
    }, [pointPeriod, pointDetailOpen, originOrganization?.id]);

    // 포인트 필터된 로그
    const pointFilteredLogs = useMemo(() => {
        if (!pointLogData || pointLogData.length === 0) return [];

        let filtered = [...pointLogData];

        // 유형 필터 적용
        if (pointTypeFilter !== -1) {
            filtered = filtered.filter(log => log.type === pointTypeFilter);
        }

        // 사용자 이름 매핑
        const filteredWithUsers = filtered.map(log => {
            const user = allUsers.find(u => u.id === log.userId);
            return {
                ...log,
                userName: user?.name || '-'
            };
        });

        // 날짜순 정렬 (최신순)
        filteredWithUsers.sort((a, b) => {
            const dateA = new Date(a.time || 0);
            const dateB = new Date(b.time || 0);
            return dateB - dateA;
        });

        return filteredWithUsers;
    }, [pointLogData, pointTypeFilter, allUsers]);

    // 사용포인트와 누적포인트 계산 (reserved for future use)
    const _pointSummary = useMemo(() => {
        const usedPoints = pointFilteredLogs
            .filter(log => log.type === 2 && (log.point < 0 || log.point === 0))
            .reduce((sum, log) => sum + Math.abs(log.point || 0), 0);

        const totalPoints = pointFilteredLogs
            .filter(log => log.type === 0 && log.point > 0)
            .reduce((sum, log) => sum + (log.point || 0), 0);

        return {
            usedPoints,
            totalPoints,
        };
    }, [pointFilteredLogs]);
    void _pointSummary;

    // 경험치 차트 데이터 생성
    const expMonthChartData = useMemo(() => {
        const year = expCurrentDate.getFullYear();
        const month = expCurrentDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const chartDataMap = new Map();

        // 항상 해당 월의 모든 날짜를 포함하도록 초기화 (데이터가 없어도 x축이 표시되도록)
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            chartDataMap.set(dateStr, { day: dateStr, value: 0 });
        }

        // 실제 데이터가 있으면 값 업데이트
        if (expPointLogData && expPointLogData.length > 0) {
            expPointLogData.forEach(log => {
                if (log.time) {
                    const dateStr = log.time.substring(0, 10);
                    if (chartDataMap.has(dateStr)) {
                        chartDataMap.get(dateStr).value += log.point || 0;
                    }
                }
            });
        }

        // 항상 배열을 반환 (빈 배열이 되지 않도록 보장)
        return Array.from(chartDataMap.values());
    }, [expPointLogData, expCurrentDate]);

    // 경험치 Y축 데이터
    const expYAxisData = useMemo(() => {
        if (!expMonthChartData || expMonthChartData.length === 0) {
            // 데이터가 없어도 축을 표시하기 위해 최소 범위 설정
            return { maxValue: 1000, minValue: 0, averageValue: 0 };
        }

        const values = expMonthChartData.map(item => item.value);
        const maxValue = Math.max(...values, 0);
        const minValue = 0;
        const averageValue = values.reduce((sum, val) => sum + val, 0) / values.length;

        // maxValue가 0이면 축이 제대로 표시되지 않으므로 최소값 설정
        return {
            maxValue: maxValue > 0 ? maxValue : 1000,
            minValue,
            averageValue
        };
    }, [expMonthChartData]);

    // 경험치 필터된 로그
    const expFilteredPointLogs = useMemo(() => {
        if (!expPointLogData || expPointLogData.length === 0) return [];

        const selectedYear = expCurrentDate.getFullYear();
        const selectedMonth = expCurrentDate.getMonth();

        return expPointLogData.filter(log => {
            if (!log.time) return false;
            const logDate = new Date(log.time);
            return logDate.getFullYear() === selectedYear && logDate.getMonth() === selectedMonth;
        });
    }, [expPointLogData, expCurrentDate]);

    const handleExpPrevMonth = () => {
        setExpCurrentDate(new Date(expCurrentDate.getFullYear(), expCurrentDate.getMonth() - 1, 1));
    };

    const handleExpNextMonth = () => {
        const now = new Date();
        const nextMonth = new Date(expCurrentDate.getFullYear(), expCurrentDate.getMonth() + 1, 1);

        if (nextMonth.getFullYear() < now.getFullYear() ||
            (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() <= now.getMonth())) {
            setExpCurrentDate(nextMonth);
        }
    };

    const expCurrentDateString = formatDateTimeDisplay(expCurrentDate, 'yearMonth');
    const now = new Date();
    const expNextMonth = new Date(expCurrentDate.getFullYear(), expCurrentDate.getMonth() + 1, 1);
    const isExpNextDisabled = expNextMonth.getFullYear() > now.getFullYear() ||
        (expNextMonth.getFullYear() === now.getFullYear() && expNextMonth.getMonth() > now.getMonth());

    // 차트 날짜 네비게이션 핸들러
    const handleChartPrevPeriod = () => {
        if (chartPeriod === 'monthly') {
            setChartCurrentDate(prev => {
                const newDate = new Date(prev);
                newDate.setMonth(prev.getMonth() - 1);
                return newDate;
            });
        } else {
            setChartCurrentDate(prev => {
                const newDate = new Date(prev);
                newDate.setFullYear(prev.getFullYear() - 1);
                return newDate;
            });
        }
    };

    const handleChartNextPeriod = () => {
        const now = new Date();
        if (chartPeriod === 'monthly') {
            const nextMonth = new Date(chartCurrentDate.getFullYear(), chartCurrentDate.getMonth() + 1, 1);
            if (nextMonth.getFullYear() < now.getFullYear() ||
                (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() <= now.getMonth())) {
                setChartCurrentDate(nextMonth);
            }
        } else {
            const nextYear = chartCurrentDate.getFullYear() + 1;
            if (nextYear <= now.getFullYear()) {
                setChartCurrentDate(new Date(nextYear, chartCurrentDate.getMonth(), 1));
            }
        }
    };

    // 차트 날짜 문자열 생성
    const chartCurrentDateString = useMemo(() => {
        if (chartPeriod === 'monthly') {
            return formatDateTimeDisplay(chartCurrentDate, 'yearMonth');
        } else {
            return formatDateTimeDisplay(chartCurrentDate, 'year');
        }
    }, [chartCurrentDate, chartPeriod, i18n.language]);

    // 이전 기간 날짜 문자열 생성 (Legend용)
    const chartPreviousDateString = useMemo(() => {
        if (chartPeriod === 'monthly') {
            const prevDate = new Date(chartCurrentDate.getFullYear(), chartCurrentDate.getMonth() - 1, 1);
            return formatDateTimeDisplay(prevDate, 'yearMonth');
        } else {
            return formatDateTimeDisplay(new Date(chartCurrentDate.getFullYear() - 1, 0, 1), 'year');
        }
    }, [chartCurrentDate, chartPeriod, i18n.language]);

    // 차트 Next 버튼 비활성화 여부
    const isChartNextDisabled = useMemo(() => {
        const now = new Date();
        if (chartPeriod === 'monthly') {
            const nextMonth = new Date(chartCurrentDate.getFullYear(), chartCurrentDate.getMonth() + 1, 1);
            return nextMonth.getFullYear() > now.getFullYear() ||
                (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() > now.getMonth());
        } else {
            const nextYear = chartCurrentDate.getFullYear() + 1;
            return nextYear > now.getFullYear();
        }
    }, [chartCurrentDate, chartPeriod]);

    if (loading) {
        return (
            <ContentArea padding='20px 10px 10px 10px'>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </ContentArea>
        );
    }

    if (error || !organization) {
        return (
            <ContentArea padding='20px 10px 10px 10px'>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Typography color={theme.palette.figma.white[60]}>{error || t('organization_information_not_found')}</Typography>
                </Box>
            </ContentArea>
        );
    }

    const divisionInfo = formatDivisionSections();
    const orgType = ORGANIZATION_TYPES.find(t => t.value === organization.type);
    const region = getRegionOptions(organization.country).find(r => r.value === organization.region);

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            <Box sx={{ px: 3, pb: 4, width: '100%', minWidth: 0, overflowX: 'auto' }}>
                {/* 헤더 */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.figma.white[80] }}>
                            {organization.name}
                        </Typography>
                        <IconButton onClick={handleEdit} sx={{ color: theme.palette.figma.white[80] }}>
                            <EditIcon />
                        </IconButton>
                    </Box>
                </Box>

                <Grid container spacing={4} sx={{ minWidth: 0 }}>
                    {/* 왼쪽 컬럼 - 기관 정보 테이블 및 사용자 랭킹 */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 600 }}>
                        {/* 기관 정보 테이블 */}
                        <CommonCard sx={{ overflow: 'auto' }}>
                            <TableContainer>
                                <Table size="small" sx={{ width: '100%', minWidth: 'auto' }}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('country')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{organization.country === 'ko' ? t('south_korea') : organization.country}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('region')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{region ? toDisplayString(t(region.label), region.label) : organization.region || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('organization_type')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{orgType ? toDisplayString(t(orgType.label), orgType.label) : '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('division_1')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{divisionInfo.div1}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('division_2')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{divisionInfo.div2}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('organization_created_date')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{formatDateTimeDisplay(organization.createDate, 'datetime')}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('didim_id')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{stats.didimIds}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('total_members')}
                                            </TableCell>
                                            <TableCell colSpan={2}>{originOrganization.memberCount}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('exp')}
                                            </TableCell>
                                            <TableCell >
                                                {(organization?.exp || 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', boxSizing: 'border-box' }}>
                                                    <Button
                                                        size="small"
                                                        onClick={handleOpenExpDetail}
                                                    >
                                                        {t('view_detail')}
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('active_championships_in_organization')}
                                            </TableCell>
                                            <TableCell>
                                                {stats.activeChallenges}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', boxSizing: 'border-box' }}>
                                                    <Button
                                                        size="small"
                                                        sx={{ minWidth: '80px' }}
                                                        onClick={() => {
                                                            // 어드민 여부 확인                                                 
                                                            if (isAdmin(meInfo?.grade)) {
                                                                navigate('/championships');
                                                            } else {
                                                                navigate('/championships', {
                                                                    state: {
                                                                        organizationId: organization?.id
                                                                    }
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        {t('view_detail')}
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CommonCard>

                        {/* 사용자 랭킹 테이블 */}
                        <CommonCard sx={{ minWidth: 0, alignItems: 'stretch', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, width: '100%', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Dropdown
                                        options={getDivisionTypeOptions()}
                                        value={selectedDivisionType}
                                        onChange={(opt) => {
                                            setSelectedDivisionType(opt.value);
                                            setSelectedDivision1('all');
                                            setSelectedDivision2('all');
                                        }}
                                        sx={{ minWidth: '120px' }}
                                    />
                                    {selectedDivisionType === 'division_1' && (
                                        <Dropdown
                                            options={getDivision1Options()}
                                            value={selectedDivision1}
                                            onChange={(opt) => setSelectedDivision1(opt.value)}
                                            sx={{ minWidth: '120px' }}
                                        />
                                    )}
                                    {selectedDivisionType === 'division_2' && (
                                        <>
                                            {getDivision1Options().length > 0 && (
                                                <Dropdown
                                                    options={getDivision1Options()}
                                                    value={selectedDivision1}
                                                    onChange={(opt) => {
                                                        setSelectedDivision1(opt.value);
                                                        setSelectedDivision2('all');
                                                    }}
                                                    sx={{ minWidth: '120px' }}
                                                />
                                            )}
                                            {selectedDivision1 && selectedDivision1 !== 'all' && getDivision2Options().length > 0 && (
                                                <Dropdown
                                                    options={getDivision2Options()}
                                                    value={selectedDivision2}
                                                    onChange={(opt) => setSelectedDivision2(opt.value)}
                                                    sx={{ minWidth: '120px' }}
                                                />
                                            )}
                                        </>
                                    )}
                                </Box>
                                <Toggle
                                    options={RANKING_TYPE_TOGGLE_OPTIONS.map(o => t(o.label))}
                                    value={rankingTypeIndex}
                                    onChange={setRankingTypeIndex}
                                    size="small"
                                    sx={{ width: 'auto', minWidth: '200px' }}
                                />
                            </Box>
                            <TableContainer sx={{ overflowX: 'auto', width: '100%', minWidth: 0 }}>
                                <Table size="small" sx={{ minWidth: 0 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('ranking')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('name')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('division_1')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('division_2')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('exp')}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t('organization_contribution')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredUserRankings.length > 0 ? (
                                            filteredUserRankings.map((user, idx) => (
                                                <TableRow
                                                    key={idx}
                                                    sx={{
                                                        ...getTableStyles(theme).tableRowStyle,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.figma.white[10],
                                                        }
                                                    }}
                                                    onClick={() => {
                                                        if (user.userId) {
                                                            // 이미 가져온 allUsers에서 사용자 정보 찾기
                                                            const userData = allUsers.find(u => u.id === user.userId);
                                                            if (userData) {
                                                                navigate('/users/detail', {
                                                                    state: {
                                                                        userData,
                                                                        organizationId: originOrganization?.id // 기관 ID 전달
                                                                    }
                                                                });
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <TableCell>{user.rank}</TableCell>
                                                    <TableCell>{user.userName}</TableCell>
                                                    <TableCell>{user.division}</TableCell>
                                                    <TableCell>{user.section}</TableCell>
                                                    <TableCell>
                                                        {user.point !== null && user.point !== undefined
                                                            ? user.point.toLocaleString()
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.contribution !== null && user.contribution !== undefined
                                                            ? `${user.contribution.toFixed(2)}%`
                                                            : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography sx={{ color: theme.palette.figma.white[60] }}>
                                                        {t('no_search_results')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CommonCard>
                    </Grid>

                    {/* 오른쪽 컬럼 - 지표 및 차트 */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <CommonCard>
                            {/* 주요 지표 */}
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                                    <MetricCard sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, minHeight: '32px' }}>
                                            <Dropdown
                                                options={ORGANIZATION_RANKING_TYPES}
                                                value={organizationRankingType}
                                                onChange={(opt) => setOrganizationRankingType(opt.value)}
                                                sx={{ minWidth: '140px' }}
                                            />
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>
                                            {organizationRanking?.toString() || '-'}
                                        </Typography>
                                    </MetricCard>
                                </Grid>

                                <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                                    <MetricCard onClick={handleOpenPointDetail} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, minHeight: '32px' }}>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80], fontSize: '14px' }}>
                                                {t('available_points')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>
                                            {(organization?.point || 0).toLocaleString()}
                                        </Typography>
                                    </MetricCard>
                                </Grid>
                            </Grid>
                        </CommonCard>



                        <CommonCard >
                            <Stack spacing={3} sx={{ width: '100%' }}>
                                {/* 레벨 */}

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                Lv
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>
                                                {organization?.level || 1}
                                            </Typography>
                                        </Box>
                                        <OrganizationEmblem level={organization?.level || 1} size={70} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[0]?.display || `${progressBars[0]?.current.toLocaleString()}${progressBars[0]?.unit}`}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[0]?.target.toLocaleString()}{progressBars[0]?.unit}
                                            </Typography>
                                        </Box>
                                        <ProgressBar
                                            rangeStart={progressBars[0]?.min || 0}
                                            rangeEnd={progressBars[0]?.target || 1}
                                            start={progressBars[0]?.min || 0}
                                            end={progressBars[0]?.current || 0}
                                            current={progressBars[0]?.current || 0}
                                            showCurrentIndicator={false}
                                            showProgress={true}
                                            height="8px"
                                            borderRadius="4px"
                                            backgroundColor={theme.palette.figma.white[20]}
                                            progressColor={theme.palette.figma.steps}
                                        />


                                    </Box>
                                </Box>

                                {/* 금일 디딤참가자수 */}

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                            {t('today_didim_participants')}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>
                                            {stats.todayParticipants}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[1]?.display || `${progressBars[1]?.current.toLocaleString()}${progressBars[1]?.unit}`}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[1]?.target.toLocaleString()}{progressBars[1]?.unit}
                                            </Typography>
                                        </Box>
                                        <ProgressBar
                                            rangeStart={progressBars[1]?.min || 0}
                                            rangeEnd={progressBars[1]?.target || 1}
                                            start={progressBars[1]?.min || 0}
                                            end={progressBars[1]?.current || 0}
                                            current={progressBars[1]?.current || 0}
                                            showCurrentIndicator={false}
                                            showProgress={true}
                                            height="8px"
                                            borderRadius="4px"
                                            backgroundColor={theme.palette.figma.white[20]}
                                            progressColor={theme.palette.figma.steps}
                                        />
                                    </Box>
                                </Box>


                                {/* 금일 디딤총사용시간 */}

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                            {t('today_didim_total_usage_time')}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>
                                            {formatTime(stats.todayTotalUsage, t)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[2]?.display || `${progressBars[2]?.current.toLocaleString()}${progressBars[2]?.unit}`}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[2]?.target.toLocaleString()}{progressBars[2]?.unit}
                                            </Typography>
                                        </Box>
                                        <ProgressBar
                                            rangeStart={progressBars[2]?.min || 0}
                                            rangeEnd={progressBars[2]?.target || 1}
                                            start={progressBars[2]?.min || 0}
                                            end={progressBars[2]?.current || 0}
                                            current={progressBars[2]?.current || 0}
                                            showCurrentIndicator={false}
                                            showProgress={true}
                                            height="8px"
                                            borderRadius="4px"
                                            backgroundColor={theme.palette.figma.white[20]}
                                            progressColor={theme.palette.figma.steps}
                                        />
                                    </Box>
                                </Box>


                                {/* 금일 평균발디딤수 */}

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                            {t('today_average_steps')}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>
                                            {stats.todayAvgSteps.toLocaleString()}{t('steps')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[3]?.display || `${progressBars[3]?.current.toLocaleString()}${progressBars[3]?.unit}`}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.figma.white[80] }}>
                                                {progressBars[3]?.target.toLocaleString()}{progressBars[3]?.unit}
                                            </Typography>
                                        </Box>
                                        <ProgressBar
                                            rangeStart={progressBars[3]?.min || 0}
                                            rangeEnd={progressBars[3]?.target || 1}
                                            start={progressBars[3]?.min || 0}
                                            end={progressBars[3]?.current || 0}
                                            current={progressBars[3]?.current || 0}
                                            showCurrentIndicator={false}
                                            showProgress={true}
                                            height="8px"
                                            borderRadius="4px"
                                            backgroundColor={theme.palette.figma.white[20]}
                                            progressColor={theme.palette.figma.steps}
                                        />
                                    </Box>
                                </Box>

                            </Stack>
                        </CommonCard>

                        <CommonCard>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    mb: 3,
                                    flexWrap: 'wrap',
                                    width: '100%',
                                    minWidth: 0,
                                    rowGap: 2,
                                }}
                            >
                                {[
                                    { value: 'participants', label: t('org_detail_chart_participants') },
                                    { value: 'usage', label: t('total_didim_usage_time') },
                                    { value: 'steps', label: t('average_steps') },
                                    { value: 'points', label: t('organization_points_earned') },
                                ].map((tab) => (
                                    <Button
                                        key={tab.value}
                                        variant={chartTab === tab.value ? 'normal' : 'gray'}
                                        size="small"
                                        onClick={() => setChartTab(tab.value)}
                                        sx={{ position: 'relative', flexShrink: 0 }}
                                    >
                                        {toDisplayString(tab.label, tab.value)}
                                    </Button>
                                ))}
                            </Stack>

                            {/* 차트 */}
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Box
                                        onClick={handleChartPrevPeriod}
                                        sx={{
                                            width: '20px',
                                            height: '20px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Prev color={theme.palette.figma.white[80]} size={20} />
                                    </Box>
                                    <Typography variant="h6" sx={{
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: theme.palette.figma.white[80],
                                        letterSpacing: '-0.48px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {chartCurrentDateString}
                                    </Typography>
                                    <Box
                                        onClick={() => !isChartNextDisabled && handleChartNextPeriod()}
                                        sx={{
                                            width: '20px',
                                            height: '20px',
                                            cursor: isChartNextDisabled ? 'default' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            opacity: isChartNextDisabled ? 0.3 : 1
                                        }}
                                    >
                                        <Next color={theme.palette.figma.white[80]} size={20} />
                                    </Box>
                                </Box>
                                <Box sx={{
                                    '& .recharts-wrapper': {
                                        '& .recharts-cartesian-grid-horizontal line': {
                                            stroke: theme.palette.figma.white[20],
                                        },
                                        '& .recharts-cartesian-grid-vertical line': {
                                            stroke: theme.palette.figma.white[20],
                                        },
                                        '& .recharts-bar-rectangle': {
                                            '&:hover': {
                                                fillOpacity: 1,
                                            },
                                        },
                                        '& .recharts-active-bar': {
                                            fillOpacity: 1,
                                        },
                                    },
                                    '& .recharts-tooltip-cursor': {
                                        fill: 'transparent',
                                    },
                                }}>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart
                                            data={chartData}
                                            style={{ background: 'transparent' }}
                                            margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.figma.white[20]} />
                                            <XAxis
                                                dataKey="day"
                                                ticks={chartPeriod === 'monthly' ? [1, 5, 10, 15, 20, 25, 30] : undefined}
                                                tickFormatter={(value) => {
                                                    if (chartPeriod === 'monthly') {
                                                        // 월간: 1, 5, 10, 15, 20, 25, 30만 표시
                                                        const visibleDays = [1, 5, 10, 15, 20, 25, 30];
                                                        return visibleDays.includes(value) ? value.toString() : '';
                                                    }
                                                    return value.toString();
                                                }}
                                                tick={{ fill: theme.palette.figma.white[80] }}
                                            />
                                            <YAxis tick={{ fill: theme.palette.figma.white[80] }} />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: theme.palette.figma.block,
                                                    border: `1px solid ${theme.palette.figma.white[30]}`,
                                                    borderRadius: '8px',
                                                    color: theme.palette.figma.white[100],
                                                    padding: '8px 12px',
                                                }}
                                                labelStyle={{
                                                    color: theme.palette.figma.white[100],
                                                    fontWeight: 600,
                                                }}
                                                itemStyle={{
                                                    color: theme.palette.figma.white[80],
                                                }}
                                            />
                                            <Legend />
                                            <Bar
                                                dataKey="previous"
                                                fill={theme.palette.figma.white[20]}
                                                name={chartPreviousDateString}
                                                isAnimationActive={false}
                                                style={{ cursor: 'default' }}
                                            />
                                            <Bar
                                                dataKey="current"
                                                fill={theme.palette.success.main}
                                                name={chartCurrentDateString}
                                                isAnimationActive={false}
                                                style={{ cursor: 'default' }}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Box>

                            {/* 차트 컨트롤 */}
                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ width: '100%' }}>

                                <Button
                                    variant={chartPeriod === 'monthly' ? 'normal' : 'gray'}
                                    size="small"
                                    onClick={() => setChartPeriod('monthly')}
                                    sx={{ position: 'relative' }}
                                >
                                    {t('monthly')}
                                </Button>
                                <Button
                                    variant={chartPeriod === 'yearly' ? 'normal' : 'gray'}
                                    size="small"
                                    onClick={() => setChartPeriod('yearly')}
                                    sx={{ position: 'relative' }}
                                >
                                    {t('yearly')}
                                </Button>
                            </Stack>
                        </CommonCard>

                    </Grid>
                </Grid>
            </Box>

            {/* 경험치 상세보기 팝업 */}
            <Dialog
                open={expDetailOpen}
                onClose={handleCloseExpDetail}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    root: { keepMounted: false },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma.popupBG,
                        maxWidth: '1200px',
                        width: '95%',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        overflow: 'auto',
                    }}
                >
                    {/* 월별 탐색 헤더 */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                    }}>
                        <Box
                            onClick={handleExpPrevMonth}
                            sx={{
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Prev color={theme.palette.figma.white[80]} size={20} />
                        </Box>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: 500,
                            color: theme.palette.figma.white[80],
                            letterSpacing: '-0.48px',
                            whiteSpace: 'nowrap'
                        }}>
                            {expCurrentDateString}
                        </Typography>
                        <Box
                            onClick={() => !isExpNextDisabled && handleExpNextMonth()}
                            sx={{
                                width: '20px',
                                height: '20px',
                                cursor: isExpNextDisabled ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: isExpNextDisabled ? 0.3 : 1
                            }}
                        >
                            <Next color={theme.palette.figma.white[80]} size={20} />
                        </Box>
                    </Box>

                    {/* 차트와 테이블 영역 */}
                    <Grid container spacing={2}>
                        {/* 왼쪽: 차트 */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                width: '100%',
                                height: '450px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <StatisticsChart
                                    activeView="month"
                                    chartData={expMonthChartData}
                                    yAxisData={expYAxisData}
                                    barColorType="average"
                                    showAllLabels={true}
                                />
                            </Box>
                        </Grid>

                        {/* 오른쪽: 상세 내역 테이블 */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                width: '100%',
                                maxHeight: '450px',
                                overflow: 'auto',
                                border: `1px solid ${theme.palette.figma.white[30]}`,
                                borderRadius: '8px',
                            }}>
                                {/* 테이블 헤더 */}
                                <Box sx={{
                                    display: 'flex',
                                    padding: '10px 15px',
                                    borderBottom: `1px solid ${theme.palette.figma.white[30]}`,
                                    backgroundColor: theme.palette.figma.white[5],
                                }}>
                                    <Typography sx={{
                                        flex: 1,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: theme.palette.figma.white[80],
                                        textAlign: 'center'
                                    }}>
                                        획득일
                                    </Typography>
                                    <Typography sx={{
                                        flex: 1,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: theme.palette.figma.white[80],
                                        textAlign: 'center'
                                    }}>
                                        사용자
                                    </Typography>
                                    <Typography sx={{
                                        flex: 2,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: theme.palette.figma.white[80],
                                        textAlign: 'center'
                                    }}>
                                        내용
                                    </Typography>
                                    <Typography sx={{
                                        flex: 1,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: theme.palette.figma.white[80],
                                        textAlign: 'center'
                                    }}>
                                        획득경험치
                                    </Typography>
                                </Box>

                                {/* 테이블 내용 */}
                                {expFilteredPointLogs.length > 0 ? (
                                    <Box>
                                        {expFilteredPointLogs.map((log, index) => {
                                            // 사용자 이름 찾기
                                            const user = allUsers.find(u => u.id === log.userId);
                                            const userName = user?.name || '-';

                                            return (
                                                <Box
                                                    key={log.id || index}
                                                    sx={{
                                                        display: 'flex',
                                                        padding: '10px 15px',
                                                        borderBottom: index < expFilteredPointLogs.length - 1
                                                            ? `1px solid ${theme.palette.figma.white[30]}`
                                                            : 'none',
                                                    }}
                                                >
                                                    <Typography sx={{
                                                        flex: 1,
                                                        fontSize: '12px',
                                                        fontWeight: 400,
                                                        color: theme.palette.figma.white[80],
                                                        textAlign: 'center'
                                                    }}>
                                                        {log.time ? formatDateTimeDisplay(log.time, 'date') : '-'}
                                                    </Typography>
                                                    <Typography sx={{
                                                        flex: 1,
                                                        fontSize: '12px',
                                                        fontWeight: 400,
                                                        color: theme.palette.figma.white[80],
                                                        textAlign: 'center'
                                                    }}>
                                                        {userName}
                                                    </Typography>
                                                    <Typography sx={{
                                                        flex: 2,
                                                        fontSize: '12px',
                                                        fontWeight: 400,
                                                        color: theme.palette.figma.white[80],
                                                        textAlign: 'center',
                                                        padding: '0 5px'
                                                    }}>
                                                        {log.message || '-'}
                                                    </Typography>
                                                    <Typography sx={{
                                                        flex: 1,
                                                        fontSize: '12px',
                                                        fontWeight: 400,
                                                        color: theme.palette.figma.white[80],
                                                        textAlign: 'center'
                                                    }}>
                                                        {log.point || 0}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Box sx={{
                                        padding: '20px',
                                        textAlign: 'center'
                                    }}>
                                        <Typography sx={{
                                            fontSize: '14px',
                                            color: theme.palette.figma.white[60],
                                        }}>
                                            {t('no_experience_history')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{
                    padding: '20px',
                    justifyContent: 'center',
                }}>
                    <Button
                        variant="normal"
                        onClick={handleCloseExpDetail}
                        sx={{ minWidth: '120px' }}
                    >
                        {t('close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 포인트 상세보기 팝업 */}
            <Dialog
                open={pointDetailOpen}
                onClose={handleClosePointDetail}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    root: { keepMounted: false },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma.popupBG,
                        maxWidth: '800px',
                        width: '90%',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        overflow: 'auto',
                    }}
                >
                    {/* 제목 */}
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>{t('organization_point_history')}</Typography>

                    {/* 가용포인트 */}
                    <Box
                        sx={{
                            width: '100%',
                            backgroundColor: theme.palette.figma.white[10],
                            borderRadius: '10px',
                            padding: '10px',
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-start">
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: theme.palette.figma.white[60],
                                    letterSpacing: '-0.42px',
                                }}
                            >
                                {t('available_points')} : <span style={{ fontWeight: 700, color: theme.palette.figma.white[100] }}>{(organization?.point || 0).toLocaleString()}P</span>
                            </Typography>
                        </Stack>
                    </Box>


                    {/* 필터 드롭다운 (history.js 스타일) */}
                    <Stack direction="row" spacing={2} sx={{ padding: '10px', borderBottom: `1px solid ${theme.palette.figma.white[30]}` }}>
                        <Dropdown
                            options={[
                                { value: 7, label: 'last_7_days' },
                                { value: 30, label: 'last_30_days' },
                                { value: 0, label: 'all_period' },
                            ]}
                            value={pointPeriod}
                            onChange={(opt) => setPointPeriod(opt.value)}
                            sx={{ minWidth: '124px' }}
                        />
                        <Dropdown
                            options={POINT_LOG_TYPES}
                            value={pointTypeFilter}
                            onChange={(opt) => setPointTypeFilter(opt.value)}
                            sx={{ minWidth: '124px' }}
                        />
                    </Stack>

                    {/* 포인트 내역 리스트 (RewardHistoryItem 사용) */}
                    <Stack spacing={0} sx={{ width: '100%' }}>
                        {pointFilteredLogs.length > 0 ? (
                            pointFilteredLogs.map((log, index) => (
                                <RewardHistoryItem key={log.id || index} item={log} />
                            ))
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    color: theme.palette.figma.white[60],
                                }}>
                                    {t('no_point_history')}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{
                    padding: '20px',
                    justifyContent: 'center',
                }}>
                    <Button
                        variant="normal"
                        onClick={handleClosePointDetail}
                        sx={{ minWidth: '120px' }}
                    >
                        {t('close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </ContentArea >
    );
};

export default OrganizationDetail;
